<script
  lang="ts"
  setup
>
import {
  computed,
  defineAsyncComponent,
  ref,
  watch,
} from '#imports'
import { ElCarousel } from 'element-plus'
import { type ActivityIconTypeKR } from '~/api'
import { useLayout } from '~/store/layout'
import $track from '~/track/webOfficialWebsite'

const AsyncScrollLoadingAnimation = defineAsyncComponent(() => import('~/components/LottieAnimation.vue'))
const lottieData = async () => (await import('@/assets/images/activity/activity-enter-kr.json')).default
defineProps<{
  activityList: ActivityIconTypeKR[]
}>()
const isMobile = computed(() => useLayout().isMobile)
const visible = ref<boolean>(false)
// const activityList = ref<ActivityIconType[]>([])
const activeIndex = ref<number>(0)
const bannerRef = ref<InstanceType<typeof ElCarousel>>()
// 展开活动
const onClick = () => {
  visible.value = true
  $track.web_official_activity_banner_click({ template: 'KR' })
}
const onClose = () => {
  visible.value = false
  activeIndex.value = 0
  $track.web_official_activity_pop_click({ template: 'KR', item: 'close', pop_rank: 0 })
}

// 手动切换
const handleChange = (index: number) => {
  bannerRef.value?.setActiveItem(index)
}
// 监听轮播切换
const onChange = (index: number) => {
  activeIndex.value = index
  $track.web_official_activity_pop_show({ template: 'KR', pop_rank: index })
}
// 点击banner
const onBannerClick = (url: string, index: number) => {
  window.open(url, '_blank')
  $track.web_official_activity_pop_click({ template: 'KR', item: 0, pop_rank: index })
}

watch(visible, (val: boolean) => {
  if (val) {
    $track.web_official_activity_pop_show({ template: 'KR', pop_rank: 0 })
  }
})
</script>

<template>
  <div
    v-if="isMobile && visible"
    class="mask"
  />
  <AsyncScrollLoadingAnimation
    v-if="!visible && activityList?.length"
    :animation-data="lottieData"
    :class="isMobile ? 'mobile-activity-animation' : 'activity-animation'"
    autoplay
    @click="onClick"
  />
  <Transition :name="isMobile ? 'fade-in-bottom' : 'slide-fade'">
    <div
      v-if="visible"
      :class="['activity', isMobile ? 'mobile-activity' : '']"
    >
      <ElCarousel
        ref="bannerRef"
        :autoplay="false"
        arrow="hover"
        class="carousel"
        height="100%"
        indicator-position="none"
        @change="onChange"
      >
        <div
          class="close"
          @click="onClose"
        >
          <img
            v-if="isMobile"
            alt=""
            src="@/assets/images/activity/icon-close.svg"
          >
          <img
            v-else
            alt=""
            src="@/assets/images/activity/icon-unfold.svg"
          >
        </div>
        <ElCarouselItem
          v-for="(item, index) in activityList"
          :key="item.banner.deepLink"
        >
          <a
            class="activity__banner"
            href="javascript:void(0)"
            @click="onBannerClick(item.banner.deepLink!, index)"
          >
            <img
              :src="item.banner.image"
              alt="activity"
            >
          </a>
        </ElCarouselItem>
      </ElCarousel>
      <div class="tabs tabs-common">
        <div
          v-for="(item, index) in activityList"
          :key="item.banner.deepLink"
          class="tab-item"
          @click="handleChange(index)"
        >
          <img
            :src="index === activeIndex ? item.naver.image : item.naver_disa.image"
            alt="icon"
          >
          <div
            v-if="index !== activityList.length - 1"
            class="line"
          />
        </div>
      </div>
    </div>
  </Transition>
</template>

<style
  lang="less"
  scoped
>
.activity-animation {
  position: fixed;
  z-index: 9999;
  right: 46px;
  bottom: 46px;
  width: 110px;
  cursor: pointer;
}

.mobile-activity-animation {
  position: fixed;
  z-index: 998;
  right: 20px;
  bottom: 20px;
  width: 84px;
}

/*
  进入和离开动画可以使用不同
  持续时间和速度曲线。
*/
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s ease-out;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

.fade-in-bottom-enter-active {
  transition: all 0.5s ease-out;
}

.fade-in-bottom-leave-active {
  transition: all 0.5s ease-out;
}

.fade-in-bottom-enter-from,
.fade-in-bottom-leave-to {
  //transform: translate(20px,160px);
  opacity: 0;
}

.activity {
  position: fixed;
  z-index: 9999;
  right: 46px;
  bottom: 46px;

  &__banner {
    img {
      width: 100%;
    }
  }

  .carousel {
    width: 500px;
    height: 360px;
    border-radius: 24px;
    transform: rotate(0deg);
    box-shadow: 0 8px 10px rgba(0, 0, 0, 0.06);

    img {
      height: 100%;
      width: 100%;
    }
  }

  .close {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 999;
    cursor: pointer;
  }

  .tabs {
    height: 48px;
    padding: 0 94px;

    img {
      height: 24px;
      width: 24px;
      cursor: pointer;
    }
  }
}

.tabs-common {
  position: absolute;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  bottom: 0;
  width: 100%;
  gap: 4px;

  .tab-item {
    display: flex;
    justify-content: space-between;
    flex: 1;
    align-items: center;
    gap: 4px;

    &:last-child {
      flex: none;
    }
  }

  .line {
    flex: 1;
    height: 0.5px;
    background-color: #dfdfdf;
  }
}

.mobile-activity {
  position: fixed;
  right: calc(50vw - 160px) !important;
  top: calc(50vh - 200px) !important;
  width: 320px;

  .carousel {
    width: 100%;
    height: 397px;
  }

  .tabs {
    position: relative;
    bottom: 50px;
    height: 50px;
    padding: 0 40px;
  }
}

:deep(.el-carousel__arrow) {
  background-color: rgba(0, 0, 0, 0.2);
}

.mask {
  position: fixed;
  top: 0;
  bottom: 0;
  z-index: 9998;
  height: 100vh;
  width: 100vw;
  background: rgba(0, 0, 0, 0.6);
}
</style>
